import { useMutation, useQuery, useQueryClient } from "react-query";
import { useClient, useAuth } from "./auth-context";
import { useAdminStore } from "../store/adminStore";
import queryString from "querystring";
import { QuestionaireSection } from "../types/questionaire";
import { useToast } from "@chakra-ui/react";
import { StaffAssessment } from "../types/staff";
export interface Reviewee {
  dept: string;
  designation: string;
  first_name: string;
  id: string;
  inactive: boolean;
  institute_id: string;
  role: string;
  sur_name: string;
  username: string;
}
export type UserPendingReview = {
  reviewee_id: string;
  id: string;
  revieweeId: string;
  assessmentId: string;
  dept: string;
  firstName: string;
  reviewed: boolean;
  surName: string;
  people_assessments_reviewee_idTopeople: Reviewee;
};
export const useUserPendingReviews = () => {
  const client = useClient();
  const { data: user } = useAuth();
  const instituteId = user?.institute_id;

  const { data, isLoading } = useQuery<Array<UserPendingReview>>(
    "userPendingReviews",
    async () => {
      const response = await client(
        `reviewees/${instituteId}?id=${user?.id}`,
        {}
      );
      return response;
    }
  );

  return {
    reviews: data ? data : [],
    isReviewsLoading: isLoading
  };
};

export type SubmitReviewPayload = {
  assessmentId: string | null;
  revieweeId: string | null;
  answers: Array<{
    questionId: string;
    sectionId: number;
    title: string;
    answer: { ans: string; extraAns?: string };
  }>;
};

export const useStaffMembers = () => {
  const client = useClient();
  const { data: user } = useAuth();
  const matOnly = useAdminStore(state => state.matOnly);
  const instituteId = user ? user.institute_id : [];
  const { data, isLoading } = useQuery<Array<string>>(
    "likely-replacement",
    () => {
      const filtersQueryOptions = {
        matOnly
      };
      const filtersQueryString = queryString.stringify(filtersQueryOptions);
      return client(`likely-replacement/${instituteId}?${filtersQueryString}`, {
        method: "GET"
      });
    }
  );
  const response = new Set(data);
  const uniqueArray = Array.from(response);
  const likelyReplacementArr = uniqueArray?.map(value => {
    return {
      key: value as string,
      label: value as string
    };
  });
  return {
    likelyReplacement: likelyReplacementArr,
    isLoading: isLoading
  };
};

export const useSubmitReview = (editReview: string | null) => {
  const client = useClient();
  const { data: user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const selectedSchool = useAdminStore(state => state.school);
  const matOnly = useAdminStore(state => state.matOnly);
  const instituteId =
    selectedSchool && matOnly === false
      ? selectedSchool.id
      : user?.institute_id;
  const { mutate, isLoading } = useMutation(
    "submitReview",
    (payload: SubmitReviewPayload) => {
      const filtersQueryString = queryString.stringify({
        instituteId: instituteId,
        editReview: editReview === "true" ? "update" : "undefined"
      });
      return client(`submit-review?${filtersQueryString}`, {
        method: "POST",
        data: { ...payload, reviewerId: user?.id }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("reviewees");
        queryClient.invalidateQueries("userPendingReviews");

        {
          editReview === "true"
            ? queryClient.invalidateQueries("staffAssessments")
            : queryClient.invalidateQueries("reviews");
        }
        toast({
          description:
            editReview === "true"
              ? "Review has been updated"
              : "Review has been submitted",
          status: "success",
          duration: 7000,
          isClosable: true
        });
      }
    }
  );

  return {
    submitReview: mutate,
    isReviewSubmitting: isLoading
  };
};
export const useReviewQuestionSet = (assessmentId: string | null) => {
  const client = useClient();
  const { data: user } = useAuth();
  const instituteId = user?.institute_id;

  const { data = [], isLoading } = useQuery<Array<QuestionaireSection>>(
    ["review-question-set", assessmentId],
    () => {
      const filtersQueryString = queryString.stringify({
        instituteId: instituteId
      });

      return client(
        `review-question-set/${assessmentId}?${filtersQueryString}`,
        {}
      );
    }
  );

  return {
    reviewQuestionSet: data,
    isQuestionSetLoading: isLoading
  };
};

export const useEditReviewQuestionSet = (assessmentId: string | null) => {
  const client = useClient();

  // const instituteId = user?.institute_id;

  const { data = [], isLoading } = useQuery(
    ["assessment", assessmentId],
    () => {
      // const filtersQueryString = queryString.stringify({
      //   instituteId: instituteId
      // });

      return client(`assessment/${assessmentId}`, {});
    }
  );

  return {
    editReviewQuestionSet: data as StaffAssessment,
    iseditReviewQuestionSetLoading: isLoading
  };
};
